import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, FontSize, LineHeight } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Underline } from "@tiptap/extension-underline";
import { useEffect } from "react";
import { sanitizeDescription } from '@/lib/sanitize';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Heading1, Heading2, Heading3,
  Undo2, Redo2, Quote, Minus,
} from "lucide-react";

const COLORS = [
  { name: "Negro", value: "#0f172a" },
  { name: "Slate", value: "#64748b" },
  { name: "Ámbar", value: "#f59e0b" },
  { name: "Rojo", value: "#dc2626" },
  { name: "Verde", value: "#16a34a" },
  { name: "Azul", value: "#2563eb" },
];

const FONT_SIZES = [
  { label: "XS", value: "12px" },
  { label: "S",  value: "14px" },
  { label: "M",  value: "16px" },
  { label: "L",  value: "18px" },
  { label: "XL", value: "22px" },
];

const LINE_HEIGHTS = [
  { label: "Compacto", value: "1.2" },
  { label: "Normal",   value: "1.6" },
  { label: "Amplio",   value: "2" },
];

export default function RichTextEditor({ value, onChange, placeholder = "Escribe la descripción..." }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      FontSize,
      LineHeight.configure({ types: ["heading", "paragraph"] }),
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: sanitizeDescription(value),
    editorProps: {
      transformPastedHTML: sanitizeDescription,
      attributes: {
        class: "tiptap-content min-h-[180px] focus:outline-none px-4 py-3 text-sm bg-white",
        "data-placeholder": placeholder,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g. opening dialog with different product)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = sanitizeDescription(value);
    if (incoming !== current && incoming !== "<p></p>") {
      editor.commands.setContent(incoming || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setFontSize = (size) => {
    if (!size) {
      editor.chain().focus().unsetMark("textStyle").run();
      return;
    }
    editor.chain().focus().setMark("textStyle", { style: `font-size: ${size}` }).run();
  };

  const setLineHeight = (lh) => {
    const sel = editor.state.selection;
    const { from, to } = sel;
    editor.chain().focus().command(({ tr, dispatch }) => {
      tr.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === "paragraph" || node.type.name === "heading") {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, lineHeight: lh });
        }
      });
      if (dispatch) dispatch(tr);
      return true;
    }).run();
  };

  return (
    <div className="border border-slate-300 bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <Group>
          <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrita" testId="rte-bold">
            <Bold className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Cursiva" testId="rte-italic">
            <Italic className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Subrayado" testId="rte-underline">
            <UnderlineIcon className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Tachado" testId="rte-strike">
            <Strikethrough className="w-3.5 h-3.5" />
          </Btn>
        </Group>

        <Group>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Título 1" testId="rte-h1">
            <Heading1 className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Título 2" testId="rte-h2">
            <Heading2 className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Título 3" testId="rte-h3">
            <Heading3 className="w-3.5 h-3.5" />
          </Btn>
        </Group>

        <Group>
          <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Izquierda" testId="rte-align-left">
            <AlignLeft className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centrado" testId="rte-align-center">
            <AlignCenter className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Derecha" testId="rte-align-right">
            <AlignRight className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justificado" testId="rte-align-justify">
            <AlignJustify className="w-3.5 h-3.5" />
          </Btn>
        </Group>

        <Group>
          <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista" testId="rte-ul">
            <List className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada" testId="rte-ol">
            <ListOrdered className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Cita" testId="rte-quote">
            <Quote className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador" testId="rte-hr">
            <Minus className="w-3.5 h-3.5" />
          </Btn>
        </Group>

        <Group>
          <select
            onChange={(e) => setFontSize(e.target.value)}
            className="h-7 text-xs border border-slate-200 bg-white px-1 font-mono"
            title="Tamaño de texto"
            data-testid="rte-font-size"
            defaultValue=""
          >
            <option value="">Tamaño</option>
            {FONT_SIZES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select
            onChange={(e) => setLineHeight(e.target.value)}
            className="h-7 text-xs border border-slate-200 bg-white px-1 font-mono"
            title="Interlineado"
            data-testid="rte-line-height"
            defaultValue=""
          >
            <option value="">Interlineado</option>
            {LINE_HEIGHTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </Group>

        <Group>
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 px-1">Color</span>
          {COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => editor.chain().focus().setColor(c.value).run()}
              title={c.name}
              data-testid={`rte-color-${c.name.toLowerCase()}`}
              className="w-5 h-5 border border-slate-300 hover:scale-110 transition-transform"
              style={{ backgroundColor: c.value }}
            />
          ))}
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetColor().run()}
            title="Quitar color"
            data-testid="rte-color-reset"
            className="text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-slate-950 px-1"
          >
            Reset
          </button>
        </Group>

        <Group last>
          <Btn onClick={() => editor.chain().focus().undo().run()} title="Deshacer" testId="rte-undo">
            <Undo2 className="w-3.5 h-3.5" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} title="Rehacer" testId="rte-redo">
            <Redo2 className="w-3.5 h-3.5" />
          </Btn>
        </Group>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}

function Group({ children, last }) {
  return (
    <div className={`flex items-center gap-0.5 px-1 ${last ? "" : "border-r border-slate-200"}`}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, active, title, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`h-7 w-7 flex items-center justify-center border transition-colors ${
        active
          ? "bg-slate-950 text-white border-slate-950"
          : "bg-white border-transparent text-slate-700 hover:bg-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

